/**
 * VendorMark — the real brand mark for every model vendor, the way
 * competitor catalogs present engines. Marks come from simple-icons
 * (monochrome official paths, rendered in currentColor so they adapt to
 * theme); vendors without a licensed mark get a branded monogram tile.
 * Our own models get the Pixellars spark.
 *
 * Usage: <VendorMark vendor={model.vendor} className="size-5" />
 *        <VendorTile vendor={model.vendor} accent={model.ui.accent} />  — the
 *        gradient-free model-card tile (flat surface + mark + hairline edge).
 */
import {
  siAlibabadotcom,
  siBytedance,
  siElevenlabs,
  siGoogle,
  siKuaishou,
  siMinimax,
  siQwen,
  siX,
  type SimpleIcon,
} from 'simple-icons';
import { LogoMark } from './Logo';

interface VendorSpec {
  icon?: SimpleIcon;
  /** Monogram fallback letter + brand color when no licensed mark exists. */
  initial: string;
  color: string;
}

const VENDORS: Record<string, VendorSpec> = {
  Google: { icon: siGoogle, initial: 'G', color: '#4285F4' },
  ByteDance: { icon: siBytedance, initial: 'B', color: '#3C8CFF' },
  Kling: { icon: siKuaishou, initial: 'K', color: '#FF4906' }, // Kuaishou = Kling's maker
  xAI: { icon: siX, initial: 'X', color: '#9ca3af' },
  Alibaba: { icon: siAlibabadotcom, initial: 'A', color: '#FF6A00' },
  MiniMax: { icon: siMinimax, initial: 'M', color: '#E73562' },
  ElevenLabs: { icon: siElevenlabs, initial: 'E', color: '#9ca3af' },
  Qwen: { icon: siQwen, initial: 'Q', color: '#6950EF' },
  OpenAI: { initial: 'O', color: '#10A37F' }, // no licensed mark in the icon set
  BFL: { initial: 'F', color: '#4B6FFF' },
  Recraft: { initial: 'R', color: '#F97316' },
};

export function VendorMark({ vendor, className = 'size-4' }: { vendor: string; className?: string }) {
  if (vendor === 'Studio') return <LogoMark className={className} />;
  const spec = VENDORS[vendor];
  if (spec?.icon) {
    return (
      <svg viewBox="0 0 24 24" className={`${className} shrink-0`} role="img" aria-label={vendor} fill="currentColor">
        <path d={spec.icon.path} />
      </svg>
    );
  }
  // Branded monogram — consistent geometry with the real marks.
  return (
    <span
      role="img"
      aria-label={vendor}
      className={`${className} inline-flex shrink-0 select-none items-center justify-center rounded-md font-bold leading-none text-white`}
      style={{ background: spec?.color ?? 'var(--surface-3)', fontSize: '0.62em' }}
    >
      {spec?.initial ?? vendor.slice(0, 1).toUpperCase()}
    </span>
  );
}

/**
 * The model-card face: FLAT neutral tile (no gradients, per brand rule) with
 * the vendor mark centered and a hairline accent edge carrying the model's
 * identity color. Replaces generated placeholder art everywhere.
 */
export function VendorTile({ vendor, accent, className = '' }: { vendor: string; accent?: string; className?: string }) {
  return (
    <div
      className={`relative flex items-center justify-center overflow-hidden bg-surface-2 text-text ${className}`}
      style={accent ? { boxShadow: `inset 0 0 0 1px color-mix(in srgb, ${accent} 22%, transparent)` } : undefined}
      aria-hidden
    >
      <VendorMark vendor={vendor} className="size-[38%] max-h-12 max-w-12 opacity-90" />
      {accent && (
        <span className="absolute inset-x-0 bottom-0 h-0.5" style={{ background: `color-mix(in srgb, ${accent} 55%, transparent)` }} />
      )}
    </div>
  );
}
